import * as THREE from 'three';
import { resolveEntityItemAnchorWorldPosition } from './entityAnchorResolution.js';
function readConnectorEndpointSideVariant(args) {
    const { parametricState, endpointRole } = args;
    const endpointResolution = parametricState?.endpointResolution;
    if (!endpointResolution)
        return null;
    const raw = endpointRole === 'from'
        ? (endpointResolution.fromSideVariant ?? endpointResolution.sideVariant)
        : (endpointResolution.toSideVariant ?? endpointResolution.sideVariant);
    if (raw === 'min' || raw === 'max')
        return raw;
    return null;
}
function normalizeDirection(direction) {
    const vec = new THREE.Vector3(...direction);
    if (vec.lengthSq() < 1e-12) {
        vec.set(1, 0, 0);
    }
    else {
        vec.normalize();
    }
    return vec;
}
function normalizeVectorOrNull(vec) {
    if (vec.lengthSq() < 1e-12)
        return null;
    return vec.normalize();
}
function findEntityItemEntry(args) {
    const { itemKey, context } = args;
    for (const constellation of context.entityConstellations) {
        const items = context.entityItemsByCollectionKey.get(constellation.itemCollectionKey) ?? [];
        const item = items.find((candidate) => candidate.itemKey === itemKey);
        if (!item)
            continue;
        return { constellation, item };
    }
    return null;
}
function resolveCandidateAnchorPosition(args) {
    const resolved = resolveEntityItemAnchorWorldPosition(args);
    if (!resolved)
        return null;
    return new THREE.Vector3(...resolved.position);
}
function resolveHorizontalSidePair(args) {
    const { itemKey, context } = args;
    const entry = findEntityItemEntry({ itemKey, context });
    if (!entry)
        return null;
    const layout = context.entityConstellationParametricStateByKey?.[entry.constellation.constellationKey]?.layout;
    if (layout?.layoutKind !== 'spoke')
        return null;
    const spokeDirectionWorld = normalizeDirection(layout.direction).applyQuaternion(new THREE.Quaternion().setFromEuler(new THREE.Euler(...(layout.rotation ?? [0, 0, 0]))));
    const uMax = resolveCandidateAnchorPosition({ itemKey, anchorKey: 'uMaxMid', context });
    const uMin = resolveCandidateAnchorPosition({ itemKey, anchorKey: 'uMinMid', context });
    const vMax = resolveCandidateAnchorPosition({ itemKey, anchorKey: 'vMaxMid', context });
    const vMin = resolveCandidateAnchorPosition({ itemKey, anchorKey: 'vMinMid', context });
    if (!uMax || !uMin || !vMax || !vMin)
        return null;
    const uAxis = normalizeVectorOrNull(uMax.clone().sub(uMin));
    const vAxis = normalizeVectorOrNull(vMax.clone().sub(vMin));
    if (!uAxis || !vAxis)
        return null;
    const uAlignment = Math.abs(uAxis.dot(spokeDirectionWorld));
    const vAlignment = Math.abs(vAxis.dot(spokeDirectionWorld));
    return uAlignment >= vAlignment ? 'u' : 'v';
}
function anchorKeyForSideVariant(args) {
    const { sidePair, sideVariant } = args;
    if (sidePair === 'u') {
        return sideVariant === 'min' ? 'uMinMid' : 'uMaxMid';
    }
    return sideVariant === 'min' ? 'vMinMid' : 'vMaxMid';
}
export function resolveSidePairAnchorEndpoint(args) {
    const { parametricState, endpointRole, endpoint, context } = args;
    if (endpoint.endpointKind !== 'static')
        return endpoint;
    if (!endpoint.itemKey.startsWith('item:entity:'))
        return endpoint;
    const sideVariant = readConnectorEndpointSideVariant({ parametricState, endpointRole });
    if (!sideVariant)
        return endpoint;
    const sidePair = resolveHorizontalSidePair({
        itemKey: endpoint.itemKey,
        context,
    });
    if (!sidePair)
        return endpoint;
    return {
        ...endpoint,
        anchorKey: anchorKeyForSideVariant({
            sidePair,
            sideVariant,
        }),
    };
}
